import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';

import type { JSX } from 'react';

import {
  Show,
  Text,
  Stack,
  HStack,
  MenuIcon,
  BellIcon,
  ProfileIcon,
  TearDropIcon,
  DropdownMenu,
  TearDropDotIcon,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
  DropdownMenuSeparator,
} from '~/components';
import { SignOutItem } from './SignOutItem';

interface IHamburgerDropdownProps {
  onNotiClick: () => void;
  unreadNotiCount: number;
}

export function HamburgerDropdown({
  onNotiClick,
  unreadNotiCount,
}: IHamburgerDropdownProps): JSX.Element {
  const { data: session } = useSession();
  const t = useTranslations('commons');

  const ItemCounter = ({ value }: { value: number }) => {
    return (
      <DropdownMenuShortcut className='bg-desire-500 rounded-full size-5 flex items-center justify-center'>
        <Text size='xs' className='font-normal text-white'>
          {value}
        </Text>
      </DropdownMenuShortcut>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <MenuIcon className='!h-[38px] cursor-pointer' />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='z-2000 w-[260px] space-y-2'>
        <DropdownMenuItem asChild>
          <Link href='/profile/partner' className='text-md font-normal w-full'>
            {t('monetizeYourSpace')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href='/profile/expert' className='text-md font-normal w-full'>
            {t('offerYourServices')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href='/about-us' className='text-md font-normal w-full'>
            {t('aboutReach')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href='/blogs' className='text-md font-normal w-full'>
            {t('reachBlogs')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href='/profile/payment' className='text-md font-normal w-full'>
            {t('payment')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href='/profile/history' className='text-md font-normal w-full'>
            {t('history')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <Show when={session}>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setTimeout(() => {
                onNotiClick();
              }, 50);
            }}
          >
            <HStack>
              <BellIcon className='size-5' />
              <Text>{t('notifications')}</Text>
            </HStack>
            <Show when={unreadNotiCount > 0}>
              <ItemCounter value={unreadNotiCount} />
            </Show>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={'/chat'} className='w-full'>
              <HStack>
                <TearDropIcon className='size-5' />
                <Text>{t('chat')}</Text>
              </HStack>
            </Link>
          </DropdownMenuItem>
        </Show>
        <DropdownMenuItem asChild>
          <Link href='/contact-us' className='w-full'>
            <HStack>
              <TearDropDotIcon className='size-5' />
              <Stack gap={0}>
                <Text>{t('needHelp')}</Text>
                <Text size='sm' className='text-dimGray-500'>
                  {t('needHelpDescription')}
                </Text>
              </Stack>
            </HStack>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <HStack className='items-center w-full'>
            <ProfileIcon className='size-5' />
            <Link href='/profile' className='text-md font-normal w-full'>
              {t('myProfile')}
            </Link>
          </HStack>
        </DropdownMenuItem>
        <Show when={session}>
          <DropdownMenuSeparator />
          <SignOutItem />
        </Show>
        <Show when={!session}>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href='/sign-in' className='text-md font-normal w-full'>
              {t('logIn')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href='/sign-up' className='text-md font-normal w-full'>
              {t('signUp')}
            </Link>
          </DropdownMenuItem>
        </Show>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
